import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HardHat, TrendingUp, Building2, PencilRuler, FileSignature,
  Compass, ArrowRight, Menu, ClipboardCheck, Users, CalendarClock, FolderOpen, PackageCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MobileNav } from "@/components/MobileNav";
import { useSiteImages } from "@/lib/siteImages";

const HERO_IMG =
  "https://images.unsplash.com/photo-1628158088791-89567a8e84ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxzdXJ2ZXlvciUyMHRvdGFsJTIwc3RhdGlvbiUyMGNvbnN0cnVjdGlvbiUyMHNpdGV8ZW58MHx8fHwxNzg4ODcxODExfDA&ixlib=rb-4.1.0&q=85";
const CTA_IMG =
  "https://images.unsplash.com/photo-1643067574858-250c69a0f152?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxhZXJpYWwlMjBjb2FzdGxpbmUlMjB0cm9waWNhbCUyMGlzbGFuZCUyMGJhaGFtYXN8ZW58MHx8fHwxNzg4ODcxODExfDA&ixlib=rb-4.1.0&q=85";

/* ---- Custom line icons to match the reference art ---- */
const iconCls = "h-9 w-9 text-[#163323]";
const IconTopo = () => (
  <svg viewBox="0 0 48 48" className={iconCls} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M24 8c-6 2-9 7-8 13 1 5 7 7 12 5 4-2 5-7 2-11-2-3-6-3-8-1s-1 6 2 6 4-3 2-4" />
    <path d="M24 41c9-3 14-11 12-19" />
  </svg>
);
const IconVolume = () => (
  <svg viewBox="0 0 48 48" className={iconCls} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 36h36" />
    <path d="M24 14 8 36h32L24 14Z" />
    <circle cx="24" cy="24" r="0.8" fill="currentColor" stroke="none" />
    <circle cx="20" cy="30" r="0.8" fill="currentColor" stroke="none" />
    <circle cx="28" cy="30" r="0.8" fill="currentColor" stroke="none" />
    <circle cx="24" cy="33" r="0.8" fill="currentColor" stroke="none" />
  </svg>
);
const IconTripod = () => (
  <svg viewBox="0 0 48 48" className={iconCls} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="18" y="9" width="12" height="8" rx="1.5" />
    <path d="M24 6v3M24 17v4" />
    <path d="M24 21 12 42M24 21l12 21M24 27v15" />
  </svg>
);
const IconReport = () => (
  <svg viewBox="0 0 48 48" className={iconCls} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 6h14l8 8v28H14V6Z" />
    <path d="M28 6v8h8" />
    <path d="M20 34v-6M24 34v-10M28 34v-4" />
  </svg>
);

const SERVICES = [
  { name: "Construction Surveying", Icon: HardHat, desc: "Precise site control and measurements to keep construction aligned with approved plans.",
    includes: ["Building corner and footing stake-out", "Foundation and structural layout", "Control points and elevation checks", "Alignment for roads and utilities"],
    whoNeeds: ["Contractors", "Builders", "Developers", "Project managers"],
    whenRequest: "Before breaking ground and at each major construction stage.",
    documents: ["Approved building or site plans", "Foundation or engineering drawings", "Lot or parcel information"],
    deliverables: ["Staked reference points on site", "Layout and elevation confirmation", "As-marked field notes"] },
  { name: "Topographical Surveys", Icon: IconTopo, desc: "Detailed mapping of land features, elevations, boundaries, and existing site conditions.",
    includes: ["Mapping of land features and elevations", "Existing structures and utilities", "Boundaries and natural contours"],
    whoNeeds: ["Architects", "Engineers", "Developers", "Property owners"],
    whenRequest: "During the early planning and design stage of a property or development.",
    documents: ["Property location or parcel number", "Any existing surveys or deeds", "Scope of the planned project"],
    deliverables: ["Topographic map with contours", "Elevation and feature data", "Digital and printable plan"] },
  { name: "Grading & Slope Analysis", Icon: TrendingUp, desc: "Reliable grading levels and slope percentages for drainage, access, and site preparation.",
    includes: ["Grading levels and spot elevations", "Slope percentage calculations", "Drainage and access assessment"],
    whoNeeds: ["Contractors", "Civil works teams", "Developers"],
    whenRequest: "Before site preparation, earthworks, or drainage design.",
    documents: ["Site or grading plans", "Property boundaries", "Intended finished levels"],
    deliverables: ["Grading level report", "Slope percentages", "Site preparation guidance"] },
  { name: "Volume Calculations", Icon: IconVolume, desc: "Accurate cut, fill, stockpile, and material volume calculations for project planning.",
    includes: ["Cut and fill volumes", "Stockpile measurement", "Material quantity estimates"],
    whoNeeds: ["Contractors", "Earthworks teams", "Project managers"],
    whenRequest: "Before or during earthmoving and material handling.",
    documents: ["Site plans or design levels", "Area to be measured", "Material type details"],
    deliverables: ["Volume report (cut / fill / stockpile)", "Quantity summary", "Supporting measurements"] },
  { name: "Building Layouts", Icon: Building2, desc: "Accurate positioning of foundations, walls, columns, utilities, and key building elements.",
    includes: ["Positioning of foundations and walls", "Column and utility locations", "Key building element set-out"],
    whoNeeds: ["Contractors", "Builders", "Architects"],
    whenRequest: "Before and during construction of the structure.",
    documents: ["Architectural and structural drawings", "Approved site plan", "Lot information"],
    deliverables: ["Marked building layout on site", "Reference lines and offsets", "Layout confirmation notes"] },
  { name: "Project As-Builts", Icon: IconTripod, desc: "Final measurements documenting completed work and actual constructed conditions.",
    includes: ["Measurement of completed work", "Actual constructed conditions", "Comparison with the design"],
    whoNeeds: ["Owners", "Contractors", "Engineers", "Project managers"],
    whenRequest: "After construction is complete or at project close-out.",
    documents: ["Original design or building plans", "Access to the completed site"],
    deliverables: ["As-built survey plan", "Final measurements", "Record documentation"] },
  { name: "Plan Production", Icon: PencilRuler, desc: "Clear professional survey plans prepared for construction, review, and project records.",
    includes: ["Professional survey plan drafting", "Clear labelling and annotations", "Formats for review and records"],
    whoNeeds: ["Architects", "Engineers", "Owners", "Developers"],
    whenRequest: "When you need formal plans for approval, construction, or records.",
    documents: ["Survey data or field notes", "Project requirements", "Reference drawings if available"],
    deliverables: ["Finished survey plan", "Digital and print-ready files", "Revisions as agreed"] },
  { name: "Legal Descriptions", Icon: FileSignature, desc: "Precise written property descriptions supported by professional survey information.",
    includes: ["Written property descriptions", "Boundary and dimension references", "Survey-supported documentation"],
    whoNeeds: ["Property owners", "Attorneys", "Developers"],
    whenRequest: "For property transactions, records, or documentation needs.",
    documents: ["Deed or title information (with permission)", "Parcel or lot details", "Existing survey if available"],
    deliverables: ["Written legal description", "Supporting survey references", "Documentation for records"] },
  { name: "Surveying Reports", Icon: IconReport, desc: "Organized findings and technical documentation for owners, contractors, and project teams.",
    includes: ["Organized survey findings", "Technical documentation", "Clear summaries for stakeholders"],
    whoNeeds: ["Owners", "Contractors", "Project teams"],
    whenRequest: "When a documented summary of surveying work is required.",
    documents: ["Project details and scope", "Any prior survey information"],
    deliverables: ["Formal surveying report", "Findings and recommendations", "Digital and printable copy"] },
];

const Brand = ({ dark }) => (
  <Link to="/" className="flex items-center gap-3">
    <img src="/data-earth-logo.webp" alt="Data Earth Surveying" className="h-10 w-10 object-contain" />
    <span className={`font-serif leading-none ${dark ? "text-[#163323]" : "text-white"}`}>
      <span className="block text-lg font-bold tracking-[0.12em]">DATA EARTH</span>
      <span className="block text-[11px] font-medium tracking-[0.42em]">SURVEYING</span>
    </span>
  </Link>
);

export default function Services() {
  const navigate = useNavigate();
  const { img } = useSiteImages();
  const [detail, setDetail] = useState(null);
  const go = (name) => navigate(`/request?service=${encodeURIComponent(name)}`);

  const nav = [
    ["Home", "/"], ["Our Services", "/services"], ["About Us", "/about"],
    ["Projects", "/projects"], ["Request a Survey", "/request"],
    ["Contact", "/contact"],
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-[#e6eae7] bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Brand dark />
          <nav className="hidden items-center gap-7 lg:flex">
            {nav.map(([label, href]) => {
              const active = label === "Our Services";
              const cls = `relative text-sm font-medium transition-colors ${active ? "text-[#163323]" : "text-[#5a6660] hover:text-[#163323]"}`;
              return href.startsWith("/") ? (
                <Link key={label} to={href} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`} className={cls}>
                  {label}
                  {active && <span className="absolute -bottom-1.5 left-0 h-0.5 w-full rounded bg-[#c8a452]" />}
                </Link>
              ) : (
                <a key={label} href={href} className={cls}>{label}</a>
              );
            })}
          </nav>
          <div className="hidden items-center gap-3 text-right xl:flex">
            <div className="font-serif text-[10px] uppercase leading-tight tracking-[0.2em] text-[#5a6660]">
              Measuring<br />Today for a<br />Brighter Bahamas
            </div>
            <span className="h-px w-10 bg-[#c8a452]" />
          </div>
          <MobileNav buttonClassName="rounded-full bg-[#163323] p-2 text-white lg:hidden" />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src={img("services_hero")} alt="Surveyor at a construction site" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d2116] via-[#163323]/90 to-[#163323]/30" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-8 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="mb-5 block h-1 w-12 rounded bg-[#c8a452]" />
            <h1 className="font-serif text-4xl font-bold leading-[1.1] text-white sm:text-5xl">
              Surveying Services Built Around Your Project
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-white/75">
              Accurate field measurements, dependable technical information, and professional
              support from planning through completion.
            </p>
          </motion.div>
          <div className="hidden items-center justify-end lg:flex">
            <div className="text-right">
              <div className="font-serif text-xl font-semibold uppercase leading-relaxed tracking-[0.15em] text-white">
                Land.<br />People.<br />Progress.
              </div>
              <div className="mt-2 font-serif text-sm uppercase tracking-[0.15em] text-[#c8a452]">
                A Stronger Bahamas.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE SERVICES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <span className="mb-4 block h-1 w-12 rounded bg-[#c8a452]" />
        <h2 className="font-serif text-3xl font-bold text-[#163323] sm:text-4xl">Explore Our Services</h2>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {SERVICES.map((s, i) => (
            <motion.button
              key={s.name}
              data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              onClick={() => setDetail(s)}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 5) * 0.05 }}
              className="group flex flex-col rounded-lg border border-[#e6eae7] bg-white p-6 text-left transition-all hover:-translate-y-1 hover:border-[#c8a452] hover:shadow-lg hover:shadow-[#163323]/5"
            >
              <s.Icon />
              <h3 className="mt-5 font-serif text-lg font-bold text-[#163323]">{s.name}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-[#5a6660]">{s.desc}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#163323] group-hover:text-[#b38f3f]">
                Learn More
                <ArrowRight className="h-4 w-4 text-[#c8a452] transition-transform group-hover:translate-x-1" />
              </span>
            </motion.button>
          ))}
        </div>
      </section>

      {/* GUIDANCE BAND */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start gap-6 rounded-xl bg-[#f4efe3] p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-5">
            <Compass className="h-11 w-11 shrink-0 text-[#163323]" strokeWidth={1.4} />
            <span className="hidden h-14 w-px bg-[#c8a452]/50 sm:block" />
            <div>
              <h3 className="font-serif text-xl font-bold text-[#163323]">Not Sure Which Survey You Need?</h3>
              <p className="mt-1 text-sm text-[#5a6660]">
                Tell us about your property or construction project, and we'll help determine the right service.
              </p>
            </div>
          </div>
          <Button
            data-testid="get-guidance-btn"
            onClick={() => navigate("/request")}
            className="h-12 shrink-0 rounded-md bg-[#b3892f] px-6 font-semibold text-white hover:bg-[#96721f]"
          >
            Get Service Guidance <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden">
        <img src={img("services_cta")} alt="Aerial view of Bahamian coastline" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0d2116]/85" />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-6 px-4 py-14 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div>
            <span className="mb-4 block h-1 w-12 rounded bg-[#c8a452]" />
            <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">Ready to Start Your Project?</h2>
            <p className="mt-2 max-w-xl text-sm text-white/70">
              Submit your project details and receive a professional review of your surveying needs.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              data-testid="cta-request-btn"
              onClick={() => navigate("/request")}
              className="h-12 rounded-md bg-[#b3892f] px-6 font-semibold text-white hover:bg-[#96721f]"
            >
              Request a Survey <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href="mailto:dataearthsurveying@gmail.com">
              <Button variant="outline" className="h-12 w-full rounded-md border-white/40 bg-transparent px-6 font-semibold text-white hover:bg-white/10 hover:text-white sm:w-auto">
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* SERVICE DETAIL MODAL */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="service-detail" className="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
          <DialogTitle className="sr-only">{detail?.name || "Service"}</DialogTitle>
          <DialogDescription className="sr-only">Service details</DialogDescription>
          {detail && (
            <div>
              <div className="flex items-center gap-4 bg-gradient-to-b from-[#163323] to-[#0d2116] px-7 py-7 text-white">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[#c8a452]/15 text-[#c8a452] ring-1 ring-[#c8a452]/40 [&_svg]:h-7 [&_svg]:w-7 [&_svg]:text-[#c8a452]">
                  <detail.Icon />
                </span>
                <div>
                  <h2 className="font-serif text-2xl font-bold">{detail.name}</h2>
                  <p className="mt-1 text-sm text-white/70">{detail.desc}</p>
                </div>
              </div>

              <div className="space-y-6 p-7">
                {[
                  { icon: ClipboardCheck, title: "What This Service Includes", items: detail.includes },
                  { icon: Users, title: "Who Needs This Service", items: detail.whoNeeds },
                  { icon: FolderOpen, title: "Documents to Provide", items: detail.documents },
                  { icon: PackageCheck, title: "Deliverables You Can Expect", items: detail.deliverables },
                ].map((sec) => (
                  <div key={sec.title}>
                    <h3 className="flex items-center gap-2 font-serif text-base font-bold text-[#163323]">
                      <sec.icon className="h-4 w-4 text-[#b38f3f]" /> {sec.title}
                    </h3>
                    <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                      {sec.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-sm text-[#4b544f]">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c8a452]" /> {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="rounded-lg bg-[#f4efe3] p-4">
                  <h3 className="flex items-center gap-2 font-serif text-base font-bold text-[#163323]">
                    <CalendarClock className="h-4 w-4 text-[#b38f3f]" /> When to Request It
                  </h3>
                  <p className="mt-1 text-sm text-[#4b544f]">{detail.whenRequest}</p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button data-testid="request-this-service-btn" onClick={() => go(detail.name)}
                    className="h-12 flex-1 rounded-md bg-[#c1943a] font-semibold text-white hover:bg-[#a97f2c]">
                    Request This Service <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <a href="mailto:dataearthsurveying@gmail.com" className="flex-1">
                    <Button variant="outline" className="h-12 w-full rounded-md border-[#d2dad5] font-semibold text-[#163323] hover:bg-[#f2f4f2]">
                      Contact Us
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* FOOTER STRIP */}
      <footer className="border-t border-[#e6eae7] bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-6 sm:px-6 lg:flex-row lg:justify-between lg:px-8">
          <Brand dark />
          <div className="flex items-center gap-4">
            <span className="hidden h-px w-20 bg-[#c8a452] sm:block" />
            <span className="font-serif text-sm italic text-[#5a6660]">Accurate. Reliable. Built for Your Project.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
